import {CONFIG} from '@/config';
import {httpService} from '@/services';
import {AxiosError, AxiosResponse} from 'axios';
import {CountryEnum, CountryType} from '@/enums/country.enum';

export interface IAddressService {
  locationFromLatLng(lat: number, lng: number): Promise<IAddress>;

  latLngFromZipCode(zipCode: number, country: CountryType): Promise<IAddress>;
}

export interface IAddress {
  uf: string;
  state: string;
  address: string;
  neighborhood: string;
  city: string;
  zipCode: string;
  location: ILocation;
}

export interface ILocation {
  lat: number;
  lng: number;
}

class Address implements IAddress {
  constructor(
      public uf: string,
      public state: string,
      public address: string,
      public neighborhood: string,
      public city: string,
      public zipCode: string,
      public location: Location) {
  }
}

class Location implements ILocation {
  constructor(
      public lat: number,
      public lng: number) {
  }
}

export class AddressService implements IAddressService {
  private apiKey: string = `AIzaSyCh986MyaQgsdzAxx2XXPQnNkkT6uD2LEM`;
  private apiUrl: string = `https://maps.googleapis.com/maps/api/geocode/json?key=${this.apiKey}`;
  private addressComponents: any;


  public locationFromLatLng(lat: number, lng: number): Promise<IAddress> {
    return httpService.json
        .get(`${this.apiUrl}&latlng=${lat},${lng}`)
        .then((response: AxiosResponse) => this.getAddressFromComponents(response, new Location(lat, lng)))
        .catch((err: AxiosError) => {
          throw err;
        });
  }

  public latLngFromZipCode(zipCode: number, country: CountryType = CountryEnum.Brasil): Promise<IAddress> {
    return httpService.json
        .get(`${this.apiUrl}&address=${zipCode},${country}`)
        .then(this.getLatLngFromComponents)
        .catch((err: AxiosError) => {
          throw err;
        });
  }

  public getAllCities() {
    return httpService.http
        .get(`${CONFIG.URL.NEODASH_API}/api/dap/store/city`)
        .catch((err: AxiosError) => {
          throw err;
        });
  }

  private getAddressFromComponents = (res: AxiosResponse, location: Location): IAddress => {
    this.addressComponents = res.data.results;

    const uf = {value: '', nodeName: 'administrative_area_level_1', field: 'short_name'};
    const state = {value: '', nodeName: 'administrative_area_level_1', field: 'long_name'};
    const address = {value: '', nodeName: 'route', field: 'long_name'};
    const neighborhood = {value: '', nodeName: 'sublocality', field: 'long_name'};
    const city = {value: '', nodeName: 'administrative_area_level_2', field: 'long_name'};
    const zipCode = {value: '', nodeName: 'postal_code', field: 'long_name'};
    const nodes = [uf, state, address, city, zipCode, neighborhood];

    for (const i in this.addressComponents) {
      const address = this.addressComponents[i];
      for (const j in address.addressComponents) {
        const address2: any = address.addressComponents[j];
        for (const k in nodes) {
          const node = nodes[k];
          if (node.value === '' && address2.types.filter((x: any) => x === node.nodeName).length) {
            node.value = address2[node.field];
          }
        }
      }
    }

    if (zipCode.value.length === 5 && zipCode.value.length > 0) {
      zipCode.value += '-000';
    }

    return new Address(
        uf.value,
        state.value,
        address.value,
        neighborhood.value,
        city.value,
        zipCode.value,
        location
    );
  };

  private getLatLngFromComponents = (res: AxiosResponse): IAddress => {
    this.addressComponents = res.data.results;

    const components: any = this.addressComponents.filter((x: any) => x.geometry);

    let location = new Location(0, 0);
    if (this.addressComponents.filter((x: any) => x.geometry).length > 0) {
      location = components[0].geometry.location;
    }

    const uf = {value: '', nodeName: 'administrative_area_level_1', field: 'short_name'};
    const state = {value: '', nodeName: 'administrative_area_level_1', field: 'long_name'};
    const city = {value: '', nodeName: 'administrative_area_level_2', field: 'long_name'};
    const neighborhood = {value: '', nodeName: 'administrative_area_level_4', field: 'long_name'};
    const address = {value: '', nodeName: 'route', field: 'long_name'};
    const zipCode = {value: '', nodeName: 'postal_code', field: 'long_name'};
    const nodes = [uf, state, address, city, zipCode, neighborhood];

    for (const i in this.addressComponents) {
      const address = this.addressComponents[i];
      for (const j in address.addressComponents) {
        const address2: any = address.addressComponents[j];
        for (const k in nodes) {
          const node = nodes[k];
          if (node.value === '' && address2.types.filter((x: any) => x === node.nodeName).length) {
            node.value = address2[node.field];
          }
        }
      }
    }

    if (zipCode.value.length === 5 && zipCode.value.length > 0) {
      zipCode.value += '-000';
    }

    return new Address(
        uf.value,
        state.value,
        address.value,
        neighborhood.value,
        city.value,
        zipCode.value,
        location
    );
  };
}

export const addressService = new AddressService();
